/**
 * Domain model for the Lisbon packaging demo.
 *
 * The branch this reads carries the *vendor* BrowseName for each tag, because that
 * is what the OPC UA servers actually expose and what Edge forwards untouched. Two
 * Line B tags deliberately diverge (`prod_rate_act`, `st`), and normalising them is
 * the job the platform's Contextualize layer exists to demonstrate - so this module
 * maps them back only for display, and never claims to be that layer.
 *
 * Note what is deliberately *not* derived here: whether a suspended machine is
 * blocked or starved. PackML reports only SUSPENDED; separating the two needs the
 * *infeed* level - full means it cannot pass product on, empty means nothing is
 * arriving - and that derivation belongs to the platform. A halted machine always
 * has a zero outfeed either way, so the outfeed cannot tell them apart. This UI
 * shows the raw state plus the buffers, which is precisely the gap the demo fills.
 */

/**
 * Where the tag branch is published.
 *
 * This is the governed root itself, with no `staged/` or `raw/` prefix: the demo's topics
 * have to look like a real plant's, and a `staged/` segment reads as a test artifact on
 * screen. The consequence is that this root has **two writers** - Stories publishes the
 * tags, and the platform's Contextualize layer publishes the computed outputs of PRD v0.3
 * §7.3 alongside them. They stay out of each other's way only because the `kpi` and
 * `agent` segments are reserved (see [RESERVED_MACHINE_SEGMENTS]), so this is a real
 * constraint on both sides rather than a coincidence.
 */
export const PACKAGING_PREFIX = 'BBCorp/Lisbon/Packaging'
export const PACKAGING_RAW_PREFIX = 'raw/BBCorp/Lisbon/Packaging'

export const LINES = ['line-a', 'line-b'] as const
export type LineId = (typeof LINES)[number]

/** Upstream first. Neighbour reasoning depends on this order. */
export const MACHINES = ['fill-01', 'cap-01', 'label-01', 'pack-01'] as const
export type MachineId = (typeof MACHINES)[number]

export const MACHINE_LABELS: Record<string, string> = {
  'fill-01': 'Filler',
  'cap-01': 'Capper',
  'label-01': 'Labeller',
  'pack-01': 'Case Packer',
}

export const LINE_LABELS: Record<string, string> = {
  'line-a': 'Line A',
  'line-b': 'Line B',
}

// ---------- canonical tags and their vendor BrowseNames ----------

export const TAGS = {
  state: 'state',
  actualRate: 'actual_rate',
  targetRate: 'target_rate',
  infeed: 'infeed_buffer_pct',
  outfeed: 'outfeed_buffer_pct',
  microStops: 'micro_stop_count',
  rejects: 'reject_count',
  webTension: 'web_tension',
  // OEE inputs. Published so Contextualize can compute Availability, Performance and
  // Quality; this module only mirrors them so nothing is silently dropped.
  designSpeed: 'design_speed',
  processed: 'processed_count',
  accExecute: 'acc_time_execute',
  accSuspended: 'acc_time_suspended',
  accHeld: 'acc_time_held',
  accIdle: 'acc_time_idle',
  accResetting: 'acc_time_resetting',
  accTotal: 'acc_time_total',
  bootCount: 'boot_count',
  // Cumulative kWh. Rises faster per unit as throughput falls, because a stopped
  // machine still draws its baseload.
  energy: 'energy_kwh',
  // Static metadata, published as tags rather than configured console-side so it
  // survives a rebuild and can be asserted in a test.
  assetId: 'asset_id',
  machineRole: 'machine_role',
  sequencePosition: 'sequence_position',
} as const

/** Line-level tags, published under the [LINE_SEGMENT] pseudo-machine. */
export const LINE_SEGMENT = 'line-status'

export const LINE_TAGS = {
  targetRate: 'line_target_rate',
  designSpeed: 'line_design_speed',
  sku: 'sku',
  orderId: 'order_id',
  shiftId: 'shift_id',
  shiftStart: 'shift_start_time',
  plannedProductionTime: 'planned_production_time_s',
  accPlanned: 'acc_time_planned_s',
  plannedStopActive: 'planned_stop_active',
  changeoverActive: 'changeover_active',
  packRatio: 'pack_ratio',
} as const

/** OEM Alpha naming, used on Line A and for every non-divergent Line B tag. */
const ALPHA_TO_CANONICAL: Record<string, string> = {
  StateCurrent: TAGS.state,
  ActualProductionRate: TAGS.actualRate,
  TargetProductionRate: TAGS.targetRate,
  InfeedLevel: TAGS.infeed,
  OutfeedLevel: TAGS.outfeed,
  MicroStopCount: TAGS.microStops,
  RejectCount: TAGS.rejects,
  WebTension: TAGS.webTension,
  DesignSpeed: TAGS.designSpeed,
  ProcessedCount: TAGS.processed,
  AccTimeExecute: TAGS.accExecute,
  AccTimeSuspended: TAGS.accSuspended,
  AccTimeHeld: TAGS.accHeld,
  AccTimeIdle: TAGS.accIdle,
  AccTimeResetting: TAGS.accResetting,
  AccTimeTotal: TAGS.accTotal,
  BootCount: TAGS.bootCount,
  EnergyKwh: TAGS.energy,
  AssetId: TAGS.assetId,
  MachineRole: TAGS.machineRole,
  SequencePosition: TAGS.sequencePosition,
}

/**
 * Line-level BrowseNames.
 *
 * Kept in its own map because `DesignSpeed` legitimately appears at both levels - on a
 * machine and on the line - exactly as it would in the real address space
 * (`DeviceSet.Filler_01.DesignSpeed` versus `LineStatus.DesignSpeed`). The topic's
 * machine segment is what disambiguates them, so resolution has to be segment-aware.
 */
const LINE_TO_CANONICAL: Record<string, string> = {
  TargetRate: LINE_TAGS.targetRate,
  DesignSpeed: LINE_TAGS.designSpeed,
  CurrentSKU: LINE_TAGS.sku,
  OrderId: LINE_TAGS.orderId,
  ShiftId: LINE_TAGS.shiftId,
  ShiftStartTime: LINE_TAGS.shiftStart,
  PlannedProductionTimeSeconds: LINE_TAGS.plannedProductionTime,
  AccTimePlannedSeconds: LINE_TAGS.accPlanned,
  PlannedStopActive: LINE_TAGS.plannedStopActive,
  ChangeoverActive: LINE_TAGS.changeoverActive,
  PackRatio: LINE_TAGS.packRatio,
}

/** OEM Beta's two divergent names, both KPI inputs. */
const BETA_TO_CANONICAL: Record<string, string> = {
  prod_rate_act: TAGS.actualRate,
  st: TAGS.state,
}

/** The two Line B tags whose names differ, for highlighting in the UI. */
export const DIVERGENT_VENDOR_TAGS = Object.keys(BETA_TO_CANONICAL)

export function canonicalTag(vendorTag: string, machineSegment?: string): string | null {
  if (machineSegment === LINE_SEGMENT) {
    return LINE_TO_CANONICAL[vendorTag] ?? null
  }
  return BETA_TO_CANONICAL[vendorTag] ?? ALPHA_TO_CANONICAL[vendorTag] ?? null
}

// ---------- PackML ----------

export const PACKML = {
  idle: 4,
  suspended: 5,
  execute: 6,
  held: 11,
  resetting: 15,
} as const

export interface PackMlDescriptor {
  label: string
  /** Chakra colorPalette token. */
  palette: string
}

const PACKML_DESCRIPTORS: Record<number, PackMlDescriptor> = {
  [PACKML.idle]: { label: 'Idle', palette: 'gray' },
  // Suspended is a stoppage, so it reads red like any other halt. Orange is
  // reserved for the constraint - the machine that is still running but limiting
  // the line - so the two are never confused.
  [PACKML.suspended]: { label: 'Suspended', palette: 'red' },
  [PACKML.execute]: { label: 'Producing', palette: 'green' },
  [PACKML.held]: { label: 'Held', palette: 'red' },
  [PACKML.resetting]: { label: 'Changeover', palette: 'blue' },
}

export function packMlDescriptor(state: number | null): PackMlDescriptor {
  if (state === null) {
    return { label: 'Unknown', palette: 'gray' }
  }
  return PACKML_DESCRIPTORS[state] ?? { label: `Code ${state}`, palette: 'gray' }
}

// ---------- topic parsing ----------

export interface ParsedTopic {
  line: string
  machine: string
  canonical: string
  vendorTag: string
}

/**
 * Segments that never name a machine.
 *
 * The tag branch and the platform's computed outputs share one root, so
 * `<root>/line-a/kpi/line_performance_rate` has exactly the shape of a machine tag with
 * `kpi` in the machine position. Rejecting these by name is what keeps a governed output
 * from being rendered as a machine.
 *
 * Relying on the tag name not matching a BrowseName is not enough on its own: a computed
 * leaf called `DesignSpeed` would resolve as a real canonical tag and conjure a machine
 * called `kpi` out of nothing.
 */
export const RESERVED_MACHINE_SEGMENTS = ['kpi', 'agent'] as const

/**
 * Parse `<prefix>/<line>/<machine>/<VendorBrowseName>`.
 * Returns null for anything outside the prefix, in a reserved segment, or with an
 * unrecognised tag, so neither an unrelated retained message nor a computed output can
 * corrupt the view.
 */
export function parseTopic(topic: string, prefix: string = PACKAGING_PREFIX): ParsedTopic | null {
  const base = prefix.replace(/\/$/, '')
  if (!topic.startsWith(`${base}/`)) {
    return null
  }
  const rest = topic.slice(base.length + 1).split('/')
  if (rest.length !== 3) {
    return null
  }
  const [line, machine, vendorTag] = rest
  // A computed output, not a machine - the platform owns these leaves.
  if ((RESERVED_MACHINE_SEGMENTS as readonly string[]).includes(machine)) {
    return null
  }
  const canonical = canonicalTag(vendorTag, machine)
  if (!canonical) {
    return null
  }
  return { line, machine, canonical, vendorTag }
}

// ---------- view model ----------

export interface MachineView {
  machine: string
  label: string
  state: number | null
  actualRate: number | null
  targetRate: number | null
  infeed: number | null
  outfeed: number | null
  microStops: number | null
  rejects: number | null
  webTension: number | null
  /** Performance against this machine's own target, as a percentage. */
  performance: number | null
  /** Nameplate speed - the correct OEE Performance denominator. */
  designSpeed: number | null
  /** Cumulative units processed, including rejects. */
  processed: number | null
  /** Derived: processed minus rejects. No rework, so this is simply good output. */
  good: number | null
}

/** Line-level context, from the `line-status` segment. */
export interface LineStatusView {
  sku: string | null
  orderId: string | null
  shiftId: string | null
  packRatio: number | null
  plannedStopActive: boolean
  changeoverActive: boolean
}

export interface LineView {
  line: string
  label: string
  machines: MachineView[]
  /** Line performance: instantaneous output over nameplate, as a percentage. */
  performance: number | null
  status: LineStatusView
}

function toText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/**
 * Build the per-line view model from a flat topic/value map.
 *
 * `values` is whatever `useMqttTopicValues` last flushed; unknown topics are
 * ignored and missing tags stay null rather than defaulting to zero, so a partly
 * warmed-up feed reads as "no data yet" instead of "running at 0%".
 */
export function buildLineViews(
  values: Record<string, { value: unknown }>,
  prefix: string = PACKAGING_PREFIX,
): LineView[] {
  // line -> machine -> canonical -> value
  const byLine = new Map<string, Map<string, Map<string, unknown>>>()

  for (const [topic, entry] of Object.entries(values)) {
    const parsed = parseTopic(topic, prefix)
    if (!parsed) {
      continue
    }
    const machines = byLine.get(parsed.line) ?? new Map()
    byLine.set(parsed.line, machines)
    const tags = machines.get(parsed.machine) ?? new Map()
    machines.set(parsed.machine, tags)
    tags.set(parsed.canonical, entry.value)
  }

  return LINES.filter((line) => byLine.has(line)).map((line) => {
    const machineMap = byLine.get(line)
    const machines: MachineView[] = MACHINES.filter((m) => machineMap?.has(m)).map((machine) => {
      const tags = machineMap?.get(machine)
      const actualRate = toNumber(tags?.get(TAGS.actualRate))
      const targetRate = toNumber(tags?.get(TAGS.targetRate))
      const processed = toNumber(tags?.get(TAGS.processed))
      const rejects = toNumber(tags?.get(TAGS.rejects))
      return {
        machine,
        label: MACHINE_LABELS[machine] ?? machine,
        state: toNumber(tags?.get(TAGS.state)),
        actualRate,
        targetRate,
        infeed: toNumber(tags?.get(TAGS.infeed)),
        outfeed: toNumber(tags?.get(TAGS.outfeed)),
        microStops: toNumber(tags?.get(TAGS.microStops)),
        rejects: toNumber(tags?.get(TAGS.rejects)),
        webTension: toNumber(tags?.get(TAGS.webTension)),
        designSpeed: toNumber(tags?.get(TAGS.designSpeed)),
        processed,
        good: processed !== null && rejects !== null ? processed - rejects : processed,
        performance:
          actualRate !== null && targetRate !== null && targetRate > 0
            ? (actualRate / targetRate) * 100
            : null,
      }
    })

    const statusTags = machineMap?.get(LINE_SEGMENT)
    return {
      line,
      label: LINE_LABELS[line] ?? line,
      machines,
      performance: linePerformance(machines),
      status: {
        sku: toText(statusTags?.get(LINE_TAGS.sku)),
        orderId: toText(statusTags?.get(LINE_TAGS.orderId)),
        shiftId: toText(statusTags?.get(LINE_TAGS.shiftId)),
        packRatio: toNumber(statusTags?.get(LINE_TAGS.packRatio)),
        plannedStopActive: statusTags?.get(LINE_TAGS.plannedStopActive) === true,
        changeoverActive: statusTags?.get(LINE_TAGS.changeoverActive) === true,
      },
    }
  })
}

/**
 * **Instantaneous** line performance: what the line is delivering right now, measured at
 * its *output* - the last machine - against that machine's own nameplate, which keeps it
 * a unit-invariant ratio even though the case packer counts cases rather than bottles.
 *
 * Bursty by nature: a throttled machine runs at nameplate and stops intermittently, so
 * this reads either ~100% or ~0%. Use [rollingLinePerformance] for anything a human
 * reads, which is what the PRD's rolling window means.
 */
export function linePerformance(machines: MachineView[]): number | null {
  const output = machines[machines.length - 1]
  if (!output) {
    return null
  }
  const denominator = output.designSpeed ?? output.targetRate
  if (output.actualRate === null || denominator === null || denominator <= 0) {
    return null
  }
  return (output.actualRate / denominator) * 100
}

/** One observation of a line's cumulative output. */
export interface OutputSample {
  at: number
  units: number
  designSpeed: number
}

/**
 * Rolling line performance from cumulative counter deltas, which is how OEE is actually
 * computed and the only stable way to read a line whose machines stop intermittently.
 *
 * Returns null until the samples span [minSpanMs], rather than reporting a confident
 * number from a window shorter than one stop cycle.
 */
export function rollingLinePerformance(samples: OutputSample[], minSpanMs = 30_000): number | null {
  if (samples.length < 2) {
    return null
  }
  const first = samples[0]
  const last = samples[samples.length - 1]
  const spanMs = last.at - first.at
  if (spanMs < minSpanMs || last.designSpeed <= 0) {
    return null
  }
  const capable = last.designSpeed * (spanMs / 60_000)
  if (capable <= 0) {
    return null
  }
  return ((last.units - first.units) / capable) * 100
}

/** Performance band, used for threshold colouring against the 90% target. */
export function performancePalette(performance: number | null): string {
  if (performance === null) {
    return 'gray'
  }
  if (performance >= 95) {
    return 'green'
  }
  if (performance >= 90) {
    return 'yellow'
  }
  return 'red'
}
